/**
 * Microsoft sign-in for the web app.
 *
 * The API Worker is an OAuth 2.1 server (api/src/auth/oauth.ts) that signs
 * people in with their March Microsoft account. This app is one of its
 * clients: a public client using PKCE, whose client id is the metadata
 * document it publishes at /oauth/client.json. Tokens are kept in
 * localStorage; the access token lives an hour and is refreshed with the
 * refresh token, which also re-checks the Microsoft account — a disabled
 * account is signed out within the hour.
 *
 * Local dev (API on localhost) skips all of this; api.ts sends X-Dev-Email,
 * which the API honours only on localhost.
 */
const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";
export const DEV_AUTH = !API_BASE || /\/\/(localhost|127\.0\.0\.1)/.test(API_BASE);

const STORE = "mia.tokens";
const PENDING = "mia.pending";
const clientId = () => `${location.origin}/oauth/client.json`;
const redirectUri = () => `${location.origin}/auth/callback`;
const resource = () => `${API_BASE}/api`;

interface Tokens {
  access: string;
  refresh?: string;
  expiresAt: number; // ms
}

function load(): Tokens | null {
  try {
    const raw = localStorage.getItem(STORE);
    return raw ? (JSON.parse(raw) as Tokens) : null;
  } catch {
    return null;
  }
}
function save(t: Tokens | null) {
  try {
    if (t) localStorage.setItem(STORE, JSON.stringify(t));
    else localStorage.removeItem(STORE);
  } catch {
    /* private mode: tokens only live for this page */
  }
}

function b64url(bytes: ArrayBuffer | Uint8Array): string {
  return btoa(String.fromCharCode(...new Uint8Array(bytes))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
const random = () => b64url(crypto.getRandomValues(new Uint8Array(32)));

export const isSignedIn = () => DEV_AUTH || load() != null;

/** Send the browser to Microsoft sign-in (via the API's /authorize). */
export async function signIn(): Promise<never> {
  const verifier = random();
  const state = random();
  const challenge = b64url(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier)));
  sessionStorage.setItem(
    PENDING,
    JSON.stringify({ verifier, state, back: location.pathname + location.search }),
  );
  const q = new URLSearchParams({
    response_type: "code",
    client_id: clientId(),
    redirect_uri: redirectUri(),
    code_challenge: challenge,
    code_challenge_method: "S256",
    state,
    scope: "inventory",
    resource: resource(),
  });
  location.assign(`${API_BASE}/authorize?${q}`);
  return new Promise<never>(() => {});
}

async function tokenRequest(fields: Record<string, string>): Promise<Tokens> {
  const res = await fetch(`${API_BASE}/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({ client_id: clientId(), resource: resource(), ...fields }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error_description || body.error || `Sign-in failed (${res.status})`);
  return {
    access: body.access_token,
    refresh: body.refresh_token,
    expiresAt: Date.now() + (Number(body.expires_in) || 3600) * 1000,
  };
}

/**
 * Finish sign-in on /auth/callback. Returns the path to go back to, or
 * throws with a message to show.
 */
export async function completeSignIn(): Promise<string> {
  const q = new URLSearchParams(location.search);
  const pending = JSON.parse(sessionStorage.getItem(PENDING) ?? "null") as
    | { verifier: string; state: string; back: string }
    | null;
  sessionStorage.removeItem(PENDING);
  if (q.get("error")) throw new Error(q.get("error_description") || q.get("error")!);
  if (!pending || q.get("state") !== pending.state || !q.get("code")) {
    throw new Error("This sign-in link has expired. Please sign in again.");
  }
  save(
    await tokenRequest({
      grant_type: "authorization_code",
      code: q.get("code")!,
      redirect_uri: redirectUri(),
      code_verifier: pending.verifier,
    }),
  );
  return pending.back && !pending.back.startsWith("/auth/") ? pending.back : "/";
}

async function refresh(stale: Tokens | null): Promise<Tokens | null> {
  const run = async () => {
    // Another tab may have refreshed while this one waited for the lock.
    const now = load();
    if (now && now.access !== stale?.access && now.expiresAt - Date.now() > 60_000) return now;
    if (!now?.refresh) return null;
    try {
      const t = await tokenRequest({ grant_type: "refresh_token", refresh_token: now.refresh });
      save(t);
      return t;
    } catch {
      save(null);
      return null;
    }
  };
  return navigator.locks ? navigator.locks.request("mia.refresh", run) : run();
}

/** A valid access token, refreshing if needed; null when signed out. */
export async function accessToken(force = false): Promise<string | null> {
  const t = load();
  if (!t) return null;
  if (!force && t.expiresAt - Date.now() > 60_000) return t.access;
  return (await refresh(t))?.access ?? null;
}

export async function signOut(): Promise<void> {
  const t = load();
  save(null);
  if (t?.refresh) {
    // Revoke the grant so the refresh token is dead even if it was copied.
    await fetch(`${API_BASE}/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({ token: t.refresh, token_type_hint: "refresh_token", client_id: clientId() }),
    }).catch(() => {});
  }
}
